/**
 * ClientErrorsPanel — admin tab that surfaces JavaScript errors
 * captured from staff sessions by the `logger.error` pipeline.
 *
 * Why this exists
 * ---------------
 * Without this, "the page froze for one staff" is a black box. With
 * this, every client-side crash carries its message, stack, URL, and
 * the user who hit it — straight to the admin's dashboard.
 *
 * UX notes
 * --------
 *  • Default view is unresolved errors only (the work queue).
 *  • Each row shows count + first/last seen so we can tell "happened
 *    twice last week" vs "happening 50 times an hour right now".
 *  • Expand a row to see the full stack trace, last URL, and the
 *    staff member who triggered the most recent occurrence.
 *  • Mark resolved → clears it from the work queue. The same error
 *    happening again will create a fresh row (the backend dedup
 *    requires `resolved: false`).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { api } from '../../lib/store';
import { toast } from 'sonner';
import {
  AlertTriangle, RefreshCw, CheckCircle2, RotateCcw, Trash2, ChevronDown, ChevronRight,
  Bug, Loader2, ExternalLink, User as UserIcon,
} from 'lucide-react';

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function ErrorRow({ row, onResolve, onUnresolve, busy }) {
  const [expanded, setExpanded] = useState(false);
  const isError = row.level === 'error';
  return (
    <div
      className={`border rounded-lg ${isError ? 'border-red-300 bg-red-50/40' : 'border-amber-300 bg-amber-50/40'}`}
      data-testid={`client-error-row-${row.id}`}
    >
      <button
        type="button"
        className="w-full flex items-start gap-2 p-3 text-left hover:bg-black/[0.02]"
        onClick={() => setExpanded((x) => !x)}
        data-testid={`client-error-expand-${row.id}`}
      >
        {expanded ? <ChevronDown className="w-4 h-4 mt-0.5 shrink-0 text-[#5b6450]" /> : <ChevronRight className="w-4 h-4 mt-0.5 shrink-0 text-[#5b6450]" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              variant={isError ? 'destructive' : 'outline'}
              className={isError ? 'bg-red-600 text-white text-[10px]' : 'border-amber-600 text-amber-700 text-[10px]'}
            >
              {row.level.toUpperCase()}
            </Badge>
            <span className="font-medium text-sm text-[#1f2a16] break-words">{row.message || '(no message)'}</span>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-[#5b6450] mt-1 flex-wrap">
            <span><strong>×{row.count}</strong> occurrences</span>
            <span>· last {fmtDate(row.last_seen)}</span>
            {row.first_seen !== row.last_seen && <span>· first {fmtDate(row.first_seen)}</span>}
            {row.last_user_name && (
              <span className="inline-flex items-center gap-1">
                · <UserIcon className="w-3 h-3" /> {row.last_user_name}
                {row.last_user_role && <span className="text-[#8F9485]">({row.last_user_role})</span>}
              </span>
            )}
          </div>
        </div>
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-dashed border-[#1f2a16]/10 space-y-2">
          {row.stack && (
            <div>
              <p className="text-[10px] uppercase font-semibold text-[#5b6450] mb-1">Stack trace</p>
              <pre className="text-[11px] bg-white border border-[#1f2a16]/10 rounded p-2 overflow-x-auto whitespace-pre-wrap max-h-60 overflow-y-auto">
                {row.stack}
              </pre>
            </div>
          )}
          {row.last_url && (
            <p className="text-[11px] text-[#5b6450] inline-flex items-center gap-1 break-all">
              <ExternalLink className="w-3 h-3 shrink-0" />
              <span className="font-medium">URL:</span>
              <span className="truncate">{row.last_url}</span>
            </p>
          )}
          {row.user_agent && (
            <p className="text-[10px] text-[#8F9485] break-all">UA: {row.user_agent}</p>
          )}
          {row.resolved && row.resolved_by && (
            <p className="text-[11px] text-emerald-700">
              Resolved by <strong>{row.resolved_by}</strong> on {fmtDate(row.resolved_at)}
            </p>
          )}
          <div className="flex gap-2 pt-1">
            {row.resolved ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onUnresolve(row.id)}
                disabled={busy}
                className="h-7 text-xs"
                data-testid={`client-error-unresolve-${row.id}`}
              >
                <RotateCcw className="w-3 h-3 mr-1" /> Reopen
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => onResolve(row.id)}
                disabled={busy}
                className="h-7 text-xs bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid={`client-error-resolve-${row.id}`}
              >
                <CheckCircle2 className="w-3 h-3 mr-1" /> Mark resolved
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ClientErrorsPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [showResolved, setShowResolved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listClientErrors({ show_resolved: showResolved, limit: 200 });
      setRows(res.data.errors || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load errors');
    } finally {
      setLoading(false);
    }
  }, [showResolved]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleResolve = async (id) => {
    setBusyId(id);
    try {
      await api.resolveClientError(id);
      toast.success('Marked as resolved');
      await fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resolve');
    } finally {
      setBusyId(null);
    }
  };

  const handleUnresolve = async (id) => {
    setBusyId(id);
    try {
      await api.unresolveClientError(id);
      toast.success('Reopened');
      await fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reopen');
    } finally {
      setBusyId(null);
    }
  };

  const handleClearResolved = async () => {
    if (!window.confirm('Permanently delete all resolved errors?')) return;
    try {
      const res = await api.clearResolvedClientErrors();
      toast.success(`Deleted ${res.data.deleted} resolved errors`);
      await fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear');
    }
  };

  return (
    <Card className="bg-[#F5F2E8] border-[#1f2a16]/15" data-testid="client-errors-panel">
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3">
        <CardTitle className="flex items-center gap-2 text-[#1f2a16] text-base">
          <Bug className="w-4 h-4 text-red-600" />
          Client errors
          <Badge variant="outline" className="ml-1 text-[11px] border-[#1f2a16]/30">
            {total}
          </Badge>
        </CardTitle>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <Switch
              id="show-resolved"
              checked={showResolved}
              onCheckedChange={setShowResolved}
              data-testid="client-errors-show-resolved-toggle"
            />
            <Label htmlFor="show-resolved" className="text-xs text-[#5b6450] cursor-pointer">
              Include resolved
            </Label>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={fetchRows}
            disabled={loading}
            className="h-8 text-xs"
            data-testid="client-errors-refresh-btn"
          >
            {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Refresh
          </Button>
          {showResolved && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleClearResolved}
              className="h-8 text-xs border-red-500 text-red-700 hover:bg-red-50"
              data-testid="client-errors-clear-btn"
            >
              <Trash2 className="w-3 h-3 mr-1" /> Clear resolved
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {!loading && rows.length === 0 && (
          <div className="text-center py-10 text-[#5b6450]">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-600" />
            <p className="text-sm font-medium">No {showResolved ? '' : 'unresolved '}errors.</p>
            <p className="text-xs text-[#8F9485]">Everything looks healthy on the client side.</p>
          </div>
        )}
        {rows.map((row) => (
          <ErrorRow
            key={row.id}
            row={row}
            onResolve={handleResolve}
            onUnresolve={handleUnresolve}
            busy={busyId === row.id}
          />
        ))}
        {!loading && rows.length > 0 && (
          <p className="text-[10px] text-[#8F9485] pt-2 text-center">
            Showing newest first. Each row groups repeats of the same error (de-duped server-side).
          </p>
        )}
      </CardContent>
    </Card>
  );
}
